import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Package, DollarSign } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAnalytics = () => {
    const analytics = {
      totalProducts: products.length,
      totalValue: 0,
      brandBreakdown: {},
      categoryBreakdown: {},
      stockLevels: { high: 0, medium: 0, low: 0, outOfStock: 0 },
      topProducts: []
    };

    products.forEach(product => {
      const totalStock = product.variants.reduce((sum, variant) => sum + variant.stock_quantity, 0);
      const productValue = totalStock * product.price;
      
      analytics.totalValue += productValue;
      
      // Brand breakdown
      analytics.brandBreakdown[product.brand] = (analytics.brandBreakdown[product.brand] || 0) + 1;
      
      // Category breakdown
      analytics.categoryBreakdown[product.category] = (analytics.categoryBreakdown[product.category] || 0) + 1;
      
      // Stock levels
      if (totalStock === 0) analytics.stockLevels.outOfStock++;
      else if (totalStock <= product.low_stock_threshold) analytics.stockLevels.low++;
      else if (totalStock <= product.low_stock_threshold * 2) analytics.stockLevels.medium++;
      else analytics.stockLevels.high++;
      
      // Top products by value
      analytics.topProducts.push({
        name: product.name,
        brand: product.brand,
        totalStock,
        value: productValue,
        image: product.image_urls[0]
      });
    });

    analytics.topProducts.sort((a, b) => b.value - a.value).slice(0, 5);
    
    return analytics;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const analytics = getAnalytics();

  return (
    <div className="space-y-8" data-testid="analytics">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Analytics & Reports</h1>
        <p className="text-gray-600 mt-2">Insights into your inventory performance and trends</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{analytics.totalProducts}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inventory Value</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">${analytics.totalValue.toLocaleString()}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Brands</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{Object.keys(analytics.brandBreakdown).length}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <BarChart3 className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Categories</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{Object.keys(analytics.categoryBreakdown).length}</p>
            </div>
            <div className="bg-amber-100 p-3 rounded-lg">
              <TrendingUp className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Brand Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Products by Brand</h2>
          <div className="space-y-4">
            {Object.entries(analytics.brandBreakdown).map(([brand, count]) => (
              <div key={brand} className="flex items-center justify-between">
                <span className="font-medium text-gray-900">{brand}</span>
                <div className="flex items-center space-x-3">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 w-32">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${(count / analytics.totalProducts) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600 w-8 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stock Levels */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Stock Level Distribution</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-green-700">High Stock</span>
              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-gray-200 rounded-full h-2 w-32">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${(analytics.stockLevels.high / analytics.totalProducts) * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600 w-8 text-right">{analytics.stockLevels.high}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-blue-700">Medium Stock</span>
              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-gray-200 rounded-full h-2 w-32">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${(analytics.stockLevels.medium / analytics.totalProducts) * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600 w-8 text-right">{analytics.stockLevels.medium}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-amber-700">Low Stock</span>
              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-gray-200 rounded-full h-2 w-32">
                  <div
                    className="bg-amber-600 h-2 rounded-full"
                    style={{ width: `${(analytics.stockLevels.low / analytics.totalProducts) * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600 w-8 text-right">{analytics.stockLevels.low}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-red-700">Out of Stock</span>
              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-gray-200 rounded-full h-2 w-32">
                  <div
                    className="bg-red-600 h-2 rounded-full"
                    style={{ width: `${(analytics.stockLevels.outOfStock / analytics.totalProducts) * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600 w-8 text-right">{analytics.stockLevels.outOfStock}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Products by Value */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Top Products by Inventory Value</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="text-left py-3 font-semibold text-gray-900">Product</th>
                <th className="text-left py-3 font-semibold text-gray-900">Brand</th>
                <th className="text-left py-3 font-semibold text-gray-900">Stock</th>
                <th className="text-right py-3 font-semibold text-gray-900">Total Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {analytics.topProducts.map((product, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="py-4">
                    <div className="flex items-center space-x-3">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                      <span className="font-medium text-gray-900">{product.name}</span>
                    </div>
                  </td>
                  <td className="py-4 text-gray-900">{product.brand}</td>
                  <td className="py-4 text-gray-900">{product.totalStock} units</td>
                  <td className="py-4 text-right font-semibold text-gray-900">
                    ${product.value.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analytics;